import React, { useState } from "react";
import Breadcrumb from "../../../Components/Common/Breadcrumb";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import Table from "../../../Components/Common/Table";
import TableHead from "../../../Components/Common/TableHead";
import RoleRow from "./Components/RoleRow";
import { RoleApiUrls } from "../../../Api/Auth/ApiUrls";
import useSWR from "swr";
import Modal from "../../../Components/Common/Modal";
import RoleForm from "./Components/RoleForm";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../Api/swrConfig";
import useToastError from "../../../hooks/useErrorToast";
import { toast } from "react-toastify";
import { Loader } from "../../../Components/Common/Loader";
import NoData from "../../../Components/Common/NoData";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import useTable from "../../../hooks/useTable";
import { useLocation } from "react-router-dom";
import QueryString from "qs";

const tableColumnData = [
  { id: "roleId", label: "No" },
  { id: "roleName", label: "Role Name" },
  { id: "description", label: "Description" },
  { id: "createdAt", label: "Created" },
];

const RoleList = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    roleName: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const { showErrorToast } = useToastError();
  const [row, setRow] = useState(null);
  const location = useLocation();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });

  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          roleName: "",
          description: "",
        });
        setModalOpen(true);
      },
    },
  ];

  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };
  const apiQueryString = QueryString.stringify(apiQueryParams);

  const { isLoading, data, mutate } = useSWR(RoleApiUrls.list(apiQueryString));

  const handleInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };
  const onEditClick = (row) => {
    setRow(row);
    handleInputChange("roleName", row.roleName);
    handleInputChange("description", row.description);
    setModalOpen(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.roleName) newErrors.roleName = "Role Name is required";
    // Add more validation logic as needed
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { trigger, isMutating } = useSWRMutation(
    RoleApiUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const { trigger: EditTrigger, isMutating: editMutating } = useSWRMutation(
    RoleApiUrls.edit(row?._id),
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    row ? EditTrigger(formData) : trigger(formData);
  };

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className={`divide-y divide-gray-200`}>
          {!isLoading ? (
            <>
              {data?.data.map((row) => (
                <RoleRow
                  key={row._id}
                  row={row}
                  mutate={mutate}
                  onEditClick={onEditClick}
                />
              ))}
            </>
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={row ? "Edit Role" : "Create Role"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        formWidth
      >
        <RoleForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
        />
      </Modal>
    </div>
  );
};

export default RoleList;
